import { useMemo, useState } from 'react';

import { DROPS, PRODUCTS, TYPES } from '@/data/products';
import type { ProductState } from '@/types';

import ArchiveDropCell from './components/ArchiveDropCell';
import DropPanel from './components/DropPanel';
import ProductCard from './components/ProductCard';
import ShopFilters, { type SortKey, type TypeFilter } from './components/ShopFilters';
import ShopHero from './components/ShopHero';
import styles from './ShopPage.module.css';

const STATE_ORDER: Record<ProductState, number> = {
  live: 0,
  low: 1,
  coming: 2,
  soldout: 3,
  archived: 4,
};

export default function ShopPage() {
  const [type, setType] = useState<TypeFilter>('ALL');
  const [showArchive, setShowArchive] = useState(false);
  const [sort, setSort] = useState<SortKey>('DROP');

  const live = useMemo(() => PRODUCTS.filter((p) => p.state !== 'archived'), []);
  const archived = useMemo(() => PRODUCTS.filter((p) => p.state === 'archived'), []);

  const filtered = useMemo(() => {
    let arr = (showArchive ? archived : live).slice();
    if (type !== 'ALL') arr = arr.filter((p) => p.type === type);
    if (sort === 'PRICE ASC') arr.sort((a, b) => a.price - b.price);
    if (sort === 'PRICE DESC') arr.sort((a, b) => b.price - a.price);
    if (sort === 'STATE') arr.sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state]);
    return arr;
  }, [type, sort, showArchive, live, archived]);

  const drop = DROPS['07Y'];
  const totalCount = live.length;
  const liveCount = live.filter((p) => p.state === 'live' || p.state === 'low').length;
  const soldCount = live.filter((p) => p.state === 'soldout').length;

  return (
    <section className={styles.root}>
      <ShopHero
        dropCode={drop.code}
        totalCount={totalCount}
        liveCount={liveCount}
        soldCount={soldCount}
      />
      <DropPanel drop={drop} />
      <ShopFilters
        types={TYPES}
        type={type}
        onTypeChange={setType}
        sort={sort}
        onSortChange={setSort}
        showArchive={showArchive}
        onToggleArchive={() => setShowArchive((v) => !v)}
      />

      <div className={styles.grid}>
        {filtered.map((p, i) => (
          <ProductCard key={p.id} p={p} idx={i + 1} />
        ))}
      </div>

      {!showArchive && (
        <>
          <header className={styles.arcHeader}>
            <span className="num">/ 02</span>
            <span className="title">ARCHIVE / PAST DROPS</span>
            <span className="meta">{Object.keys(DROPS).length} DROPS · NOT FOR SALE</span>
          </header>
          <div className={styles.arcGrid}>
            {Object.values(DROPS).map((d) => (
              <ArchiveDropCell key={d.id} drop={d} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
